import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AdminNav } from "@/components/admin/AdminNav";
import {
  Archive,
  Trash2,
  FolderPlus,
  Inbox as InboxIcon,
  Folder,
  FolderInput,
  RotateCcw,
  MailOpen,
  Mail,
} from "lucide-react";

type Submission = {
  id: string;
  name: string;
  email: string;
  message: string;
  created_at: string;
  ip_address: string | null;
  read: boolean;
  folder: string;
  read_at: string | null;
};
type FolderRow = { id: string; name: string };

const SYSTEM = [
  { key: "inbox", label: "Inbox", icon: InboxIcon },
  { key: "archived", label: "Archived", icon: Archive },
  { key: "trash", label: "Trash", icon: Trash2 },
];

export const Route = createFileRoute("/admin/inbox")({
  head: () => ({
    meta: [
      { title: "Admin | Inbox | Scott Cunningham" },
      { name: "description", content: "Inbox for Scott Cunningham’s website administration." },
      { property: "og:title", content: "Admin | Inbox | Scott Cunningham" },
      { property: "og:description", content: "Inbox for Scott Cunningham’s website administration." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Admin | Inbox | Scott Cunningham" },
      { name: "twitter:description", content: "Inbox for Scott Cunningham’s website administration." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: InboxPage,
});

function InboxPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Submission[]>([]);
  const [folders, setFolders] = useState<FolderRow[]>([]);
  const [currentFolder, setCurrentFolder] = useState<string>("inbox");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [activeId, setActiveId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate({ to: "/login" });
  }, [user, loading, navigate]);

  const reload = async () => {
    setBusy(true);
    const [a, b] = await Promise.all([
      supabase
        .from("contact_submissions")
        .select("id, name, email, message, created_at, ip_address, read, folder, read_at")
        .order("created_at", { ascending: false })
        .limit(500),
      supabase.from("admin_folders").select("id, name").order("name"),
    ]);
    setRows((a.data as Submission[]) ?? []);
    setFolders((b.data as FolderRow[]) ?? []);
    setBusy(false);
  };

  useEffect(() => {
    if (!isAdmin) return;
    reload();
  }, [isAdmin]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (r.folder !== currentFolder) return false;
      if (!q) return true;
      return (
        r.name?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        r.message?.toLowerCase().includes(q)
      );
    });
  }, [rows, currentFolder, query]);

  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const r of rows) map[r.folder] = (map[r.folder] ?? 0) + (r.read ? 0 : 1);
    return map;
  }, [rows]);

  const active = rows.find((r) => r.id === activeId) ?? null;

  const toggleSel = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const selectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((r) => r.id)));
  };

  const applyToSelected = async (patch: Partial<Submission>) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    await supabase.from("contact_submissions").update(patch).in("id", ids);
    setRows((prev) => prev.map((r) => (selected.has(r.id) ? { ...r, ...patch } : r)));
    setSelected(new Set());
  };

  const deletePermanent = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!confirm(`Permanently delete ${ids.length} message(s)?`)) return;
    await supabase.from("contact_submissions").delete().in("id", ids);
    setRows((prev) => prev.filter((r) => !selected.has(r.id)));
    setSelected(new Set());
  };

  const openOne = async (r: Submission) => {
    setActiveId(r.id);
    if (!r.read) {
      await supabase
        .from("contact_submissions")
        .update({ read: true, read_at: new Date().toISOString() })
        .eq("id", r.id);
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, read: true } : x)));
    }
  };

  const createFolder = async () => {
    const name = prompt("Folder name?");
    if (!name) return;
    const trimmed = name.trim();
    if (!trimmed || SYSTEM.some((s) => s.key === trimmed.toLowerCase())) return;
    const { data, error } = await supabase
      .from("admin_folders")
      .insert({ name: trimmed })
      .select()
      .single();
    if (error) return alert(error.message);
    setFolders((f) => [...f, data as FolderRow].sort((a, b) => a.name.localeCompare(b.name)));
  };

  if (loading) return <div className="p-12 text-muted-foreground">Loading…</div>;
  if (!user) return null;
  if (!isAdmin) return <div className="p-12 text-muted-foreground">Not authorized.</div>;

  return (
    <div className="mx-auto max-w-7xl px-6 py-10">
      <AdminNav title="Inbox" />

      <div className="grid grid-cols-12 gap-4 h-[calc(100vh-12rem)] min-h-[32rem]">
        {/* Sidebar */}
        <aside className="col-span-12 md:col-span-2 rounded-md border border-border p-2 overflow-y-auto">
          <div className="space-y-0.5">
            {SYSTEM.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => {
                  setCurrentFolder(key);
                  setSelected(new Set());
                  setActiveId(null);
                }}
                className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded text-sm ${currentFolder === key ? "bg-muted font-semibold" : "hover:bg-muted/50"}`}
              >
                <span className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  {label}
                </span>
                {counts[key] ? (
                  <span className="text-xs bg-primary text-primary-foreground rounded-full px-1.5">
                    {counts[key]}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center justify-between px-2 mb-1">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Folders
              </span>
              <button
                onClick={createFolder}
                className="text-muted-foreground hover:text-foreground"
                title="New folder"
              >
                <FolderPlus className="h-3.5 w-3.5" />
              </button>
            </div>
            {folders.length === 0 && (
              <div className="px-2 text-xs text-muted-foreground">No folders yet.</div>
            )}
            {folders.map((f) => (
              <button
                key={f.id}
                onClick={() => {
                  setCurrentFolder(f.name);
                  setSelected(new Set());
                  setActiveId(null);
                }}
                className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded text-sm ${currentFolder === f.name ? "bg-muted font-semibold" : "hover:bg-muted/50"}`}
              >
                <span className="flex items-center gap-2">
                  <Folder className="h-4 w-4" />
                  {f.name}
                </span>
                {counts[f.name] ? (
                  <span className="text-xs bg-primary text-primary-foreground rounded-full px-1.5">
                    {counts[f.name]}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </aside>

        {/* List */}
        <section className="col-span-12 md:col-span-4 rounded-md border border-border flex flex-col overflow-hidden">
          <div className="border-b border-border p-2 space-y-2">
            <Input
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8"
            />
            <div className="flex items-center gap-1 flex-wrap">
              <label className="flex items-center gap-1 text-xs px-1">
                <input
                  type="checkbox"
                  checked={selected.size > 0 && selected.size === filtered.length}
                  onChange={selectAll}
                />
                {selected.size > 0 ? `${selected.size} selected` : "Select all"}
              </label>
              {selected.size > 0 && (
                <>
                  {currentFolder !== "archived" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => applyToSelected({ folder: "archived" })}
                      title="Archive"
                    >
                      <Archive className="h-4 w-4" />
                    </Button>
                  )}
                  {currentFolder !== "trash" ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => applyToSelected({ folder: "trash" })}
                      title="Move to trash"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => applyToSelected({ folder: "inbox" })}
                        title="Restore"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={deletePermanent}
                        title="Delete permanently"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      applyToSelected({ read: true, read_at: new Date().toISOString() })
                    }
                    title="Mark read"
                  >
                    <MailOpen className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => applyToSelected({ read: false, read_at: null })}
                    title="Mark unread"
                  >
                    <Mail className="h-4 w-4" />
                  </Button>
                  <select
                    className="text-xs border border-border bg-background rounded px-1 py-1"
                    defaultValue=""
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) return;
                      applyToSelected({ folder: val });
                      e.currentTarget.value = "";
                    }}
                  >
                    <option value="">Move to…</option>
                    <option value="inbox">Inbox</option>
                    {folders.map((f) => (
                      <option key={f.id} value={f.name}>
                        {f.name}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-border">
            {busy && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
            {!busy && filtered.length === 0 && (
              <div className="p-6 text-sm text-muted-foreground">No messages.</div>
            )}
            {filtered.map((r) => (
              <div
                key={r.id}
                className={`flex gap-2 px-3 py-2 cursor-pointer hover:bg-muted/50 ${activeId === r.id ? "bg-muted" : ""} ${!r.read ? "font-semibold" : ""}`}
                onClick={() => openOne(r)}
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={selected.has(r.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleSel(r.id);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="truncate text-sm">{r.name || r.email}</div>
                    <div className="text-[10px] text-muted-foreground whitespace-nowrap">
                      {new Date(r.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{r.message}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Reader */}
        <section className="col-span-12 md:col-span-6 rounded-md border border-border overflow-hidden flex flex-col">
          {!active && (
            <div className="flex-1 grid place-items-center text-sm text-muted-foreground">
              Select a message to read.
            </div>
          )}
          {active && (
            <>
              <div className="border-b border-border p-4">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="font-display text-xl">{active.name || "(no name)"}</h2>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        await supabase
                          .from("contact_submissions")
                          .update({ folder: active.folder === "archived" ? "inbox" : "archived" })
                          .eq("id", active.id);
                        setRows((prev) =>
                          prev.map((x) =>
                            x.id === active.id
                              ? {
                                  ...x,
                                  folder: active.folder === "archived" ? "inbox" : "archived",
                                }
                              : x,
                          ),
                        );
                      }}
                    >
                      <Archive className="h-4 w-4 mr-1" />
                      {active.folder === "archived" ? "Unarchive" : "Archive"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        await supabase
                          .from("contact_submissions")
                          .update({ folder: "trash" })
                          .eq("id", active.id);
                        setRows((prev) =>
                          prev.map((x) => (x.id === active.id ? { ...x, folder: "trash" } : x)),
                        );
                        setActiveId(null);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    <select
                      className="text-xs border border-border bg-background rounded px-2 py-1.5"
                      value={active.folder}
                      onChange={async (e) => {
                        const val = e.target.value;
                        await supabase
                          .from("contact_submissions")
                          .update({ folder: val })
                          .eq("id", active.id);
                        setRows((prev) =>
                          prev.map((x) => (x.id === active.id ? { ...x, folder: val } : x)),
                        );
                      }}
                    >
                      <option value="inbox">Inbox</option>
                      <option value="archived">Archived</option>
                      <option value="trash">Trash</option>
                      {folders.map((f) => (
                        <option key={f.id} value={f.name}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-1 text-sm text-muted-foreground flex flex-wrap gap-x-3">
                  <a className="hover:underline" href={`mailto:${active.email}`}>
                    {active.email}
                  </a>
                  <span>{new Date(active.created_at).toLocaleString()}</span>
                  {active.ip_address && (
                    <span className="font-mono text-xs">{active.ip_address}</span>
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 whitespace-pre-wrap text-sm leading-relaxed">
                {active.message}
              </div>
              <div className="border-t border-border p-3 flex justify-end">
                <a href={`mailto:${active.email}?subject=Re:%20Your%20message`}>
                  <Button size="sm">
                    <FolderInput className="h-4 w-4 mr-1" />
                    Reply via email
                  </Button>
                </a>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
